import { useEffect, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { X, Plus, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { courseService } from '@/services/course.service';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select, SelectOption } from '@/components/ui/Select';
import { QuestionOptionsField } from '@/components/ui/QuestionOptionsField';
import { questionValidationSchema } from '@/validations/questionValidation';

type QuestionType = 'SINGLE_CHOICE' | 'MULTIPLE_CHOICE' | 'TRUE_FALSE' | 'TEXT_ANSWER';

interface QuestionFormData {
  question_text: string;
  question_type: QuestionType;
  options?: { id: string; text: string; is_correct: boolean; explanation?: string }[];
  answer_keywords?: string[];
  case_sensitive?: boolean;
  points: number;
  difficulty_level: string;
  bloom_level?: string;
  topic?: string;
  explanation?: string;
  hint?: string;
}

interface AddAssessmentQuestionModalProps {
  assessmentId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export default function AddAssessmentQuestionModal({
  assessmentId,
  onClose,
  onSuccess,
}: AddAssessmentQuestionModalProps) {
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<QuestionFormData>({
    mode: 'all',
    resolver: yupResolver(questionValidationSchema) as any,
    defaultValues: {
      question_text: '',
      question_type: 'SINGLE_CHOICE',
      options: [
        { id: crypto.randomUUID(), text: '', is_correct: false },
        { id: crypto.randomUUID(), text: '', is_correct: false },
      ],
      answer_keywords: [],
      case_sensitive: false,
      points: 1,
      difficulty_level: 'EASY',
      bloom_level: '',
      explanation: '',
      hint: '',
    },
  });

  const questionType = watch('question_type');
  const prevTypeRef = useRef(questionType);

  // Clear selected answers when switching between question types
  useEffect(() => {
    if (prevTypeRef.current === questionType) return;
    const prev = prevTypeRef.current;
    prevTypeRef.current = questionType;

    if (
      (prev === 'SINGLE_CHOICE' || prev === 'MULTIPLE_CHOICE') &&
      (questionType === 'SINGLE_CHOICE' || questionType === 'MULTIPLE_CHOICE')
    ) {
      const current_options = watch('options') || [];
      setValue('options', current_options.map(o => ({ ...o, is_correct: false })));
    }

    if (questionType === 'TRUE_FALSE') {
      setValue('options', [
        { id: crypto.randomUUID(), text: 'True', is_correct: false },
        { id: crypto.randomUUID(), text: 'False', is_correct: false },
      ]);
    }

    if (questionType === 'TEXT_ANSWER') {
      setValue('options', []);
      setValue('answer_keywords', []);
    }

    if (prev === 'TEXT_ANSWER' && questionType !== 'TEXT_ANSWER') {
      if (questionType !== 'TRUE_FALSE') {
        setValue('options', [
          { id: crypto.randomUUID(), text: '', is_correct: false },
          { id: crypto.randomUUID(), text: '', is_correct: false },
        ]);
      }
    }
  }, [questionType, setValue, watch]);

  const addMutation = useMutation({
    mutationFn: (question: any) =>
      courseService.addAssessmentQuestion(assessmentId, question),
    onSuccess: () => {
      toast.success('Question added successfully!');
      onSuccess();
    },
    onError: (error: any) => {
      const errorData = error.response?.data;
      if (errorData?.meta_data?.errors && Array.isArray(errorData.meta_data.errors)) {
        errorData.meta_data.errors.forEach((err: string) => {
          toast.error(err, { duration: 5000 });
        });
      } else if (errorData?.error?.error_description) {
        toast.error(errorData.error.error_description);
      } else {
        toast.error(error.message || 'Failed to add question');
      }
    },
  });

  const onSubmit = (data: QuestionFormData) => {
    const questionData: any = { ...data };

    if (data.question_type === 'TEXT_ANSWER') {
      delete questionData.options;
    } else {
      delete questionData.answer_keywords;
      delete questionData.case_sensitive;
    }

    addMutation.mutate(questionData);
  };

  const questionTypeOptions: SelectOption[] = [
    { label: 'Single Choice', value: 'SINGLE_CHOICE' },
    { label: 'Multiple Choice', value: 'MULTIPLE_CHOICE' },
    { label: 'True/False', value: 'TRUE_FALSE' },
    { label: 'Text Answer', value: 'TEXT_ANSWER' },
  ];

  const difficultyOptions: SelectOption[] = [
    { label: 'Easy', value: 'EASY' },
    { label: 'Medium', value: 'MEDIUM' },
    { label: 'Hard', value: 'HARD' },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-brand-blue-600 to-brand-indigo-600 text-white">
          <h2 className="text-2xl font-bold flex items-center gap-3">
            <Plus className="w-7 h-7" />
            Add Question
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-y-auto p-6 space-y-6">
          <Textarea
            name="question_text"
            control={control}
            label="Question Text"
            placeholder="Enter your question here..."
            rows={3}
            required
            error={errors.question_text?.message}
          />

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Select
              name="question_type"
              control={control}
              label="Question Type"
              options={questionTypeOptions}
              required
              error={errors.question_type?.message}
            />

            <Select
              name="difficulty_level"
              control={control}
              label="Difficulty"
              options={difficultyOptions}
              required
              error={errors.difficulty_level?.message}
            />

            <Input
              name="points"
              control={control}
              label="Points"
              type="number"
              min={1}
              max={100}
              onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                const val = Math.min(100, Math.max(1, Number(e.currentTarget.value) || 1));
                e.currentTarget.value = String(val);
              }}
              required
              error={errors.points?.message}
            />
          </div>

          {questionType !== 'TEXT_ANSWER' ? (
            <QuestionOptionsField
              name="options"
              control={control}
              label="Answer Options"
              questionType={questionType as QuestionType}
              minOptions={questionType === 'TRUE_FALSE' ? 2 : questionType === 'MULTIPLE_CHOICE' ? 3 : 2}
              required
              error={errors.options?.message}
            />
          ) : (
            <Controller
              name="answer_keywords"
              control={control}
              render={({ field }) => {
                const [rawText, setRawText] = useState(field.value?.join(', ') || '');

                const syncToField = (text: string) => {
                  const keywords = text
                    .split(',')
                    .map((k) => k.trim())
                    .filter((k) => k.length > 0);
                  field.onChange(keywords);
                };

                return (
                  <div className="w-full">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Answer Keywords
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <textarea
                      rows={2}
                      value={rawText}
                      onChange={(e) => {
                        setRawText(e.target.value);
                        syncToField(e.target.value);
                      }}
                      onBlur={() => {
                        const cleaned = rawText
                          .split(',')
                          .map((k) => k.trim())
                          .filter((k) => k.length > 0)
                          .join(', ');
                        setRawText(cleaned);
                      }}
                      className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 resize-y ${
                        errors.answer_keywords ? 'border-red-500 focus:ring-red-500' : 'border-gray-200'
                      }`}
                      placeholder="Enter keywords separated by commas (e.g., React, JavaScript, Framework)"
                    />
                    {errors.answer_keywords && (
                      <p className="text-red-500 text-xs mt-1">{errors.answer_keywords.message}</p>
                    )}
                    <p className="text-gray-500 text-xs mt-1">
                      Separate multiple keywords with commas.
                    </p>
                    {field.value && field.value.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        <p className="text-xs font-medium text-gray-600 w-full">Parsed Keywords:</p>
                        {field.value.map((keyword, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium"
                          >
                            {keyword}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }}
            />
          )}

          {/* Case Sensitive toggle - only for TEXT_ANSWER */}
          {questionType === 'TEXT_ANSWER' && (
            <Controller
              name="case_sensitive"
              control={control}
              render={({ field }) => (
                <label className="flex items-center gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={field.value || false}
                    onChange={(e) => field.onChange(e.target.checked)}
                    className="w-4 h-4 text-brand-indigo-600 rounded focus:ring-2 focus:ring-brand-indigo-500 cursor-pointer"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">Case sensitive matching</span>
                    <p className="text-xs text-gray-500">
                      {field.value
                        ? 'Exact case required — "React" won\'t match "react"'
                        : 'Case ignored — "React" matches "react", "REACT"'}
                    </p>
                  </div>
                </label>
              )}
            />
          )}

          <div className="grid grid-cols-1 gap-4">
            <Input
              name="bloom_level"
              control={control}
              label="Bloom Level (Optional)"
              placeholder="e.g., Understanding"
              error={errors.bloom_level?.message}
            />
          </div>

          <Textarea
            name="explanation"
            control={control}
            label="Explanation (Optional)"
            placeholder="Explain why this is the correct answer..."
            rows={2}
            error={errors.explanation?.message}
          />

          <Input
            name="hint"
            control={control}
            label="Hint (Optional)"
            placeholder="Provide a hint for learners..."
            error={errors.hint?.message}
          />
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2.5 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit(onSubmit)}
            disabled={addMutation.isPending}
            className="px-6 py-2.5 bg-brand-blue-600 text-white rounded-lg font-semibold hover:bg-brand-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {addMutation.isPending ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Adding...
              </>
            ) : (
              <>
                <Plus className="w-5 h-5" />
                Add Question
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
